/**
 * Tool Items Hook
 * 
 * Provides items from the candidates store for use in prioritization tools.
 * Handles:
 * - Loading items ready for ranking
 * - Filtering by org unit access
 * - Transforming to tool-friendly format
 * - Saving results back to focus lists
 */

import { useState, useCallback, useMemo } from 'react';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useUIStore } from '../../stores/prioritization/uiStore';
import type { Item } from '../../types/prioritization/lifecycle';
import {
  type ToolInputItem,
  type ToolRunResult,
  type LoadItemsOptions,
  type SaveToListOptions,
  type SaveToListResult,
  type ToolDefinition,
  itemsToToolInput,
  createToolRunResult,
  sortResultsByScore,
  normalizeScores,
  calculateCutLine,
  validateItemsForTool,
  getToolById,
} from '../../lib/toolIntegration';

// ============================================================================
// Types
// ============================================================================

export interface UseToolItemsOptions {
  /** Tool ID for validation */
  toolId?: string;
  
  /** Auto-load items on mount */
  autoLoad?: boolean;
  
  /** Filter options */
  filters?: LoadItemsOptions;
}

export interface UseToolItemsResult {
  // Data
  items: ToolInputItem[];
  rawItems: Item[];  // Original items for reference
  
  // Loading state
  loading: boolean;
  error: string | null;
  
  // Tool info
  tool: ToolDefinition | undefined;
  validation: { valid: boolean; errors: string[] };
  
  // Actions
  loadItems: (options?: LoadItemsOptions) => void;
  refreshItems: () => void;
  
  // Tool run management
  startToolRun: () => ToolRunResult;
  currentRun: ToolRunResult | null;
  
  // Save results
  saveToItemPool: (options: SaveToListOptions) => Promise<SaveToListResult>;
  
  // Helpers
  getItemById: (id: string) => ToolInputItem | undefined;
  getRawItemById: (id: string) => Item | undefined;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useToolItems(options: UseToolItemsOptions = {}): UseToolItemsResult {
  const { toolId, autoLoad = true, filters } = options;
  
  // Stores - use primitive selectors to avoid Zustand infinite loops
  const items = useItemsStore((state) => state.items);
  const userId = useUserStore((state) => state.userId);
  const addNotification = useUIStore((state) => state.addNotification);
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadOptions, setLoadOptions] = useState<LoadItemsOptions>(filters || {});
  const [currentRun, setCurrentRun] = useState<ToolRunResult | null>(null);
  
  // Get tool definition
  const tool = toolId ? getToolById(toolId) : undefined;
  
  // Filter candidates to items available for tools
  // Include all items (shaped candidates), not just those with readyForRanking=true
  // This allows users to work with items even before they reach 80% completeness
  const rawItems = useMemo(() => {
    let filtered = items.filter((c): c is Item => 
      c.lifecycle_state === 'item'
    );
    
    // Apply additional filters
    if (loadOptions.orgUnitId) {
      filtered = filtered.filter(item => item.orgUnitId === loadOptions.orgUnitId);
    }
    if (loadOptions.orgUnitIds?.length) {
      filtered = filtered.filter(item => 
        !item.orgUnitId || loadOptions.orgUnitIds!.includes(item.orgUnitId)
      );
    }
    if (loadOptions.tags?.length) {
      filtered = filtered.filter(item => 
        item.tags && loadOptions.tags!.some(tag => item.tags.includes(tag))
      );
    }
    if (loadOptions.requiredAttributes?.length) {
      filtered = filtered.filter(item => {
        if (!item.attributes || !Array.isArray(item.attributes)) return false;
        const itemAttrs = new Set(item.attributes.map(a => a.attributeKey));
        return loadOptions.requiredAttributes!.every(attr => itemAttrs.has(attr));
      });
    }
    if (loadOptions.limit) {
      filtered = filtered.slice(0, loadOptions.limit);
    }
    
    return filtered;
  }, [items, loadOptions]);
  
  // Transform to tool input format
  const toolItems = useMemo(() => itemsToToolInput(rawItems), [rawItems]);
  
  // Validate items for tool
  const validation = useMemo(() => {
    if (!tool) return { valid: true, errors: [] };
    return validateItemsForTool(toolItems, tool);
  }, [toolItems, tool]);
  
  // Load items with options
  const loadItems = useCallback((newOptions?: LoadItemsOptions) => {
    setLoading(true);
    setError(null);
    
    try {
      if (newOptions) {
        setLoadOptions(prev => ({ ...prev, ...newOptions }));
      }
      // Items are loaded reactively via useMemo
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load items');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Refresh items
  const refreshItems = useCallback(() => {
    loadItems(loadOptions);
  }, [loadItems, loadOptions]);
  
  // Start a new tool run
  const startToolRun = useCallback(() => {
    if (!toolId || !tool) {
      throw new Error('Tool ID required to start a tool run');
    }
    
    const run = createToolRunResult(toolId, tool.name, toolItems, userId);
    setCurrentRun(run);
    return run;
  }, [toolId, tool, toolItems, userId]);
  
  // Save results to item pool
  const saveToItemPool = useCallback(async (options: SaveToListOptions): Promise<SaveToListResult> => {
    if (!currentRun) {
      return {
        success: false,
        itemPoolId: '',
        itemPoolName: '',
        itemsAdded: 0,
        itemsUpdated: 0,
        itemsRemoved: 0,
        errors: ['No tool run in progress'],
      };
    }
    
    try {
      // Normalize and sort scores
      if (currentRun.results.some(r => r.score !== undefined)) {
        normalizeScores(currentRun);
        sortResultsByScore(currentRun);
      }
      
      // Calculate cut line
      const cutLine = calculateCutLine(currentRun, options);
      
      // For now, just return mock success
      // TODO: Actually save to Supabase
      const result: SaveToListResult = {
        success: true,
        itemPoolId: options.itemPoolId || `list-${Date.now()}`,
        itemPoolName: options.listName || `${tool?.name || 'Tool'} Results`,
        itemsAdded: currentRun.results.length,
        itemsUpdated: 0,
        itemsRemoved: 0,
      };
      
      addNotification({
        type: 'success',
        title: 'Results Saved',
        message: `Saved ${result.itemsAdded} items to "${result.itemPoolName}"`,
        duration: 5000,
      });
      
      return result;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to save results';
      addNotification({
        type: 'error',
        title: 'Save Failed',
        message: errorMsg,
        duration: 5000,
      });
      
      return {
        success: false,
        itemPoolId: '',
        itemPoolName: '',
        itemsAdded: 0,
        itemsUpdated: 0,
        itemsRemoved: 0,
        errors: [errorMsg],
      };
    }
  }, [currentRun, tool, addNotification]);
  
  // Get item by ID
  const getItemById = useCallback((id: string) => {
    return items.find(item => item.id === id);
  }, [items]);
  
  const getRawItemById = useCallback((id: string) => {
    return rawItems.find(item => item.id === id);
  }, [rawItems]);
  
  return {
    items: toolItems,
    rawItems,
    loading,
    error,
    tool,
    validation,
    loadItems,
    refreshItems,
    startToolRun,
    currentRun,
    saveToItemPool,
    getItemById,
    getRawItemById,
  };
}

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Hook for getting items for a specific tool
 */
export function useToolItemsForTool(toolId: string) {
  return useToolItems({ toolId });
}

/**
 * Hook for getting items filtered by org unit
 */
export function useOrgUnitItems(orgUnitId: string) {
  return useToolItems({
    filters: { orgUnitId },
  });
}

/**
 * Hook for getting item count ready for tools
 */
export function useToolReadyItemCount(): number {
  const { items } = useToolItems({ autoLoad: true });
  return items.length;
}
